import Select from "../../../../common/core-ui/Select.tsx";
import React, { useEffect, useRef } from "react";
import { useCoursePickerFetcher } from "./coursePickerLoader.ts";
import { useIntl } from "react-intl";

type CourseFieldSelectProps = React.ComponentPropsWithRef<typeof Select> & {
  action?: string;
  placeholder: string;
  fetcher?: ReturnType<typeof useCoursePickerFetcher>;
};

export default function CourseFieldSelect({
  action,
  placeholder,
  fetcher: overrideFetcher,
  ...selectProps
}: CourseFieldSelectProps) {
  const defaultFetcher = useCoursePickerFetcher();
  const fetcher = overrideFetcher ?? defaultFetcher;
  const selectRef = useRef<HTMLSelectElement>(null);
  const intl = useIntl();

  useEffect(() => {
    if (action && fetcher.state === "idle" && !fetcher.data) {
      void fetcher.load(action);
    }
  }, [action, fetcher]);

  const safeIterableData = fetcher.data?.data ?? [];

  function getPlaceholder() {
    if (fetcher.state !== "idle") {
      return intl.formatMessage({ defaultMessage: "Loading..." });
    }
    if (!overrideFetcher && fetcher.data && safeIterableData.length === 0) {
      return intl.formatMessage({ defaultMessage: "None found" });
    }
    if (safeIterableData.length > 0) {
      return placeholder;
    }
  }

  const actualPlaceholder = getPlaceholder();

  useEffect(() => {
    if (actualPlaceholder) {
      window.setTimeout(() => {
        selectRef.current?.focus();
      }, 10);
    }
  }, [actualPlaceholder]);

  return (
    <div>
      <Select {...selectProps} disabled={!actualPlaceholder} ref={selectRef}>
        {actualPlaceholder && (
          <option
            value=""
            data-placeholder="true"
            hidden={safeIterableData.length === 0}
          >
            {actualPlaceholder}
          </option>
        )}
        {safeIterableData.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </Select>
    </div>
  );
}
